import React, { useEffect, useState } from 'react'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Modal } from '@amzn/stencil-react-components/modal'
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner'
import { H2, H3, Text } from '@amzn/stencil-react-components/text'
import { IconCheckCircleFill } from '@amzn/stencil-react-icons'

import { CopyModuleIdsModal } from 'src/components/ModuleOptions'
import { ItemContainerMetadataDTO } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { pluralize } from 'src/pages/import-content/ImportContentModel'
import { ModuleService } from 'src/services/backend/ModuleService'

export interface PlannedContainer {
    name: string
    rowCount: number
}

export interface CreateStepProps {
    fileName?: string
    rowCount: number
    plannedContainers: PlannedContainer[]
    isSubmitting: boolean
    containers?: ItemContainerMetadataDTO[]
    hasError: boolean
    onBack: () => void
    onDone: () => void
}

interface ContainerRowProps {
    name: string
    rowCount?: number
    versionId?: string
    createdAt: Date
    onCopyIds: (versionId: string) => void
}

const MODULE_BUILDER_PATH = '/module-builder?moduleVersionId='

const formatCreatedAt = (date: Date) =>
    `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    })}`

const ContainerRow = ({ name, rowCount, versionId, createdAt, onCopyIds }: ContainerRowProps) => {
    const status = versionId ? `Created on ${formatCreatedAt(createdAt)}` : 'Creating…'

    return (
        <Row
            alignItems='center'
            gridGap='S300'
            padding='S400'
            dataTestId={`import-container-${name}`}
            style={{ border: '1px solid #E1E4EA', borderRadius: '8px' }}
        >
            {versionId ? (
                <IconCheckCircleFill
                    title='Created'
                    color='green70'
                    dataTestId={`import-container-created-${name}`}
                />
            ) : (
                <Spinner
                    size={SpinnerSize.Small}
                    dataTestId={`import-container-creating-${name}`}
                />
            )}
            <Col gridGap='S100' style={{ flex: 1 }}>
                <Text fontSize='T200' fontWeight='bold'>
                    {name}
                </Text>
                <Text fontSize='T100' color='neutral70'>
                    {rowCount === undefined ? status : `${pluralize(rowCount, 'row')} · ${status}`}
                </Text>
            </Col>
            <Button
                variant={ButtonVariant.Secondary}
                size={ButtonSize.Small}
                disabled={!versionId}
                onClick={
                    versionId
                        ? () =>
                              window.open(
                                  `${MODULE_BUILDER_PATH}${versionId}`,
                                  '_blank',
                                  'noopener,noreferrer'
                              )
                        : undefined
                }
                dataTestId={`open-module-${name}`}
            >
                Open module
            </Button>
            <Button
                variant={ButtonVariant.Tertiary}
                size={ButtonSize.Small}
                disabled={!versionId}
                onClick={versionId ? () => onCopyIds(versionId) : undefined}
                dataTestId={`copy-module-ids-${name}`}
            >
                Copy module IDs
            </Button>
        </Row>
    )
}

export const CreateStep = ({
    fileName,
    rowCount,
    plannedContainers,
    isSubmitting,
    containers,
    hasError,
    onBack,
    onDone,
}: CreateStepProps) => {
    const [createdAt, setCreatedAt] = useState(() => new Date())
    useEffect(() => setCreatedAt(new Date()), [containers])
    const [moduleIds, setModuleIds] = useState<{ id: string; version: string } | undefined>(
        undefined
    )
    const [copyIdsError, setCopyIdsError] = useState<string | undefined>(undefined)

    const onCopyIds = async (versionId: string) => {
        setCopyIdsError(undefined)
        try {
            const { id } = await ModuleService.loadModuleVersionDTO(versionId, true)
            setModuleIds({ id, version: versionId })
        } catch (e) {
            console.error('Failed to load module ids', e)
            setCopyIdsError('Failed to load the module IDs. Please try again.')
        }
    }

    const rowCountByName = new Map(
        plannedContainers.map((planned) => [planned.name, planned.rowCount])
    )
    const rows: Omit<ContainerRowProps, 'createdAt' | 'onCopyIds'>[] = containers
        ? containers.map((container) => ({
              name: container.name,
              rowCount: rowCountByName.get(container.name),
              versionId: container.versionId,
          }))
        : isSubmitting
        ? plannedContainers.map((planned) => ({ ...planned, versionId: undefined }))
        : []

    return (
        <Card isElevated width='100%' dataTestId='import-content-create-step'>
            <Col gridGap='S400' width='100%'>
                <Col gridGap='S100'>
                    <H2 fontSize='T400'>
                        {containers ? 'Module items created' : 'Creating module items'}
                    </H2>
                    {fileName && (
                        <Text fontSize='T100' color='neutral70' dataTestId='import-source'>
                            From {fileName} · {pluralize(rowCount, 'row')}
                        </Text>
                    )}
                </Col>

                {copyIdsError && (
                    <MessageBanner type={MessageBannerType.Error} dataTestId='copy-ids-error'>
                        {copyIdsError}
                    </MessageBanner>
                )}

                {containers?.length === 0 ? (
                    <Text dataTestId='import-no-containers'>
                        No Item Container Modules were generated.
                    </Text>
                ) : (
                    rows.length > 0 && (
                        <Col gridGap='S300' dataTestId='generated-item-containers'>
                            <H3 fontSize='T200'>
                                {containers ? 'Modules created' : 'Modules being created'} (
                                {rows.length})
                            </H3>
                            {rows.map((row) => (
                                <ContainerRow
                                    key={row.name}
                                    {...row}
                                    createdAt={createdAt}
                                    onCopyIds={(versionId) => void onCopyIds(versionId)}
                                />
                            ))}
                        </Col>
                    )
                )}

                <View>
                    <Row gridGap='S300'>
                        {hasError && (
                            <Button
                                dataTestId='import-content-back-to-review'
                                variant={ButtonVariant.Tertiary}
                                onClick={onBack}
                            >
                                Back to review
                            </Button>
                        )}
                        {containers && (
                            <Button
                                dataTestId='import-content-done'
                                variant={ButtonVariant.Primary}
                                onClick={onDone}
                            >
                                Back to module builder
                            </Button>
                        )}
                    </Row>
                </View>
            </Col>

            <Modal isOpen={!!moduleIds} close={() => setModuleIds(undefined)}>
                {moduleIds && (
                    <CopyModuleIdsModal
                        close={() => setModuleIds(undefined)}
                        moduleEntity={moduleIds}
                    />
                )}
            </Modal>
        </Card>
    )
}
